import type { RepartoMes } from '../../hooks/useCalendarioMes'
import { COLORES_DIA_LISTA } from '../agendamiento/coloresDia'
import { marcaDeEstado } from '../agendamiento/estadoReparto'

/**
 * Panel del DÍA seleccionado en el calendario mensual (opción "ver + añadir").
 *
 * Al pulsar una celda del mes, la página muestra este panel con los repartos ya
 * agendados ese día y un botón para añadir uno nuevo. Es presentacional: recibe
 * la lista y los callbacks; no sabe de la API ni abre el formulario por su
 * cuenta (eso lo decide la página, que ya tiene el alta de reparto de B.3).
 */

interface Props {
  /** Fecha ISO del día abierto (YYYY-MM-DD). */
  iso: string
  /** Repartos de ese día. */
  repartos: RepartoMes[]
  /** Añadir un reparto nuevo en este día. */
  onAnadir: (iso: string) => void
  /** Cerrar el panel. */
  onCerrar: () => void
}

/** Fecha ISO → texto legible "lunes, 15 de septiembre de 2026". */
function fechaLegible(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const fecha = new Date(y, m - 1, d)
  return fecha.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function FilaReparto({ reparto }: { reparto: RepartoMes }) {
  const color = reparto.ruta
    ? COLORES_DIA_LISTA.find((c) => c.clave === reparto.ruta)
    : undefined
  const marca = marcaDeEstado(reparto.estado)
  return (
    <li className="flex items-center gap-2 rounded-md border border-borde-suave bg-lienzo px-2.5 py-2">
      <span
        aria-hidden
        className={['inline-block h-2.5 w-2.5 shrink-0 rounded-sm ring-1', color?.fondo ?? 'bg-superficie', color?.anillo ?? 'ring-borde'].join(' ')}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-tinta">{reparto.cliente}</p>
        <p className="flex flex-wrap gap-x-2 text-xs text-tinta-apagada">
          {reparto.bono ? <span>{reparto.bono}</span> : null}
          {reparto.ruta ? <span className="uppercase">{reparto.ruta}</span> : null}
        </p>
      </div>
      <span className="flex shrink-0 items-center gap-1 text-xs text-tinta-2">
        <span aria-hidden>{marca.simbolo}</span>
        {marca.etiqueta}
      </span>
    </li>
  )
}

export function PanelDia({ iso, repartos, onAnadir, onCerrar }: Props) {
  const carga = repartos.length
  return (
    <aside
      aria-label={`Repartos del ${fechaLegible(iso)}`}
      className="flex flex-col gap-3 rounded-lg border border-borde bg-superficie p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold capitalize text-tinta">{fechaLegible(iso)}</h3>
          <p className="text-xs text-tinta-apagada">
            {carga === 0 ? 'Sin repartos' : `${carga} ${carga === 1 ? 'reparto' : 'repartos'}`}
          </p>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar el día"
          className="rounded-md px-2 py-1 text-sm text-tinta-2 ring-1 ring-borde hover:bg-lienzo focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
        >
          ✕
        </button>
      </div>

      {carga > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {repartos.map((r) => (
            <FilaReparto key={r.id} reparto={r} />
          ))}
        </ul>
      ) : (
        <p className="rounded-md bg-lienzo px-3 py-4 text-center text-sm text-tinta-apagada">
          No hay repartos agendados este día.
        </p>
      )}

      <button
        type="button"
        onClick={() => onAnadir(iso)}
        className="rounded-lg bg-acento px-3 py-2 text-sm font-medium text-acento-contraste hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
      >
        + Añadir reparto
      </button>
    </aside>
  )
}
